import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Send, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  X,
  Atom,
  Zap,
  Cpu,
  Wifi,
  Globe
} from 'lucide-react';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    workEmail: '',
    company: '',
    phoneNumber: '',
    country: '',
    reason: '',
    message: '',
    howDidYouHear: '',
    privacyConsent: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Comprehensive country list
  const countries = [
    'Please select', 'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 
    'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
    'Brazil', 'Bulgaria', 'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana',
    'Greece', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia',
    'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Pakistan', 'Philippines', 'Poland',
    'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
    'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand', 'Turkey', 'Ukraine',
    'United Arab Emirates', 'United Kingdom', 'United States', 'Vietnam', 'Other'
  ];

  const reasonOptions = [
    'Please select',
    "I'd like to schedule a demo",
    "I'd like to learn more about Godquantum System products", 
    "I'm an existing Godquantum system Customer and need technical support",
    "I'm interested in Careers at Godquantum Systems"
  ];

  const howDidYouHearOptions = [
    'Please select',
    'I have used godquantum system products at my Work, Lab',
    'I heard about products from a colleague',
    'Google search',
    'Conference/events',
    'Other'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'Please complete this required field.';
    if (!formData.lastName.trim()) newErrors.lastName = 'Please complete this required field.';
    if (!formData.workEmail.trim()) newErrors.workEmail = 'Please complete this required field.';
    else if (!/\S+@\S+\.\S+/.test(formData.workEmail)) newErrors.workEmail = 'Please enter a valid email address.';
    if (!formData.company.trim()) newErrors.company = 'Please complete this required field.';
    if (!formData.country || formData.country === 'Please select') newErrors.country = 'Please complete this required field.';
    if (!formData.reason || formData.reason === 'Please select') newErrors.reason = 'Please complete this required field.';
    if (!formData.message.trim()) newErrors.message = 'Please complete this required field.';
    if (formData.message.length > 5000) newErrors.message = 'Message must be under 5000 characters.';
    if (!formData.privacyConsent) newErrors.privacyConsent = 'You must consent to the privacy policy to submit.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '', lastName: '', workEmail: '', company: '', phoneNumber: '',
          country: '', reason: '', message: '', howDidYouHear: '', privacyConsent: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSubmitStatus(null);
      setErrors({});
    }
  }, [isOpen]);

  // Quantum Universe 3D Background Component (Simplified for modal)
  const QuantumUniverse3D = () => {
    const [particles, setParticles] = useState([]);

    useEffect(() => {
      // Generate fewer particles for modal performance
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.6 + 0.2,
      }));
      setParticles(newParticles);
    }, []);

    const quantumIcons = [Atom, Zap, Cpu];

    return (
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-emerald-900/20 animate-pulse" />
        
        {/* Floating Quantum Particles - Reduced for performance */}
        {particles.slice(0, 15).map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-emerald-400/40 rounded-full animate-pulse"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animationDelay: `${particle.id * 0.5}s`,
              animationDuration: '3s'
            }}
          />
        ))}

        {/* Simplified Quantum Icons */}
        {quantumIcons.map((Icon, index) => (
          <motion.div
            key={index}
            className="absolute text-emerald-400/20"
            style={{
              left: `${20 + index * 30}%`,
              top: `${30 + (index % 2) * 30}%`,
            }}
            animate={{
              rotate: [0, 360],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 8 + index * 2,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <Icon size={24} />
          </motion.div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-black/90 border border-white/10 rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <motion.button
            onClick={onClose}
            className="absolute top-6 right-6 z-20 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-5 h-5 text-white" />
          </motion.button>

          <div className="grid lg:grid-cols-2 gap-0 h-full max-h-[95vh]">
            {/* Left Side - 3D Background and Information */}
            <div className="relative bg-gradient-to-br from-black/80 to-black/60 flex items-center justify-center p-8 min-h-[400px]">
              <QuantumUniverse3D />
              
              <motion.div 
                className="text-center z-10 max-w-sm"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <motion.h3 
                  className="text-2xl font-bold mb-4 text-white"
                  animate={{ 
                    textShadow: [
                      "0 0 20px rgba(0, 255, 209, 0.5)",
                      "0 0 30px rgba(0, 255, 209, 0.3)",
                      "0 0 20px rgba(0, 255, 209, 0.5)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Contact Us
                </motion.h3>
                <div className="space-y-3 text-gray-300 text-sm">
                  <p className="leading-relaxed">
                    How can we help you and your team achieve the best results? Our quantum computing, 
                    electronics, cryogenic, and quantum physicists experts are waiting to hear from you.
                  </p>
                  <p>
                    Please fill in your details, select the products you'd like to see, and click to submit.
                  </p>
                  <p className="text-emerald-300 font-semibold">
                    A Full stack quantum computing specialist will contact you shortly.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="flex flex-col bg-black/40">
              <div className="p-6 overflow-y-auto flex-1 max-h-[calc(95vh-2rem)]">
              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div 
                    key="success"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-4 text-white">Thank You!</h3>
                    <p className="text-gray-300 mb-6">
                      Your message has been submitted successfully. Our team will contact you shortly.
                    </p>
                    <Button
                      onClick={() => setSubmitStatus(null)}
                      className="btn-primary"
                    >
                      Submit Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <motion.form 
                    key="form"
                    onSubmit={handleSubmit} 
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <h3 className="text-xl font-bold text-white mb-4">Get In Touch</h3>
                    
                    {/* Name Fields */}
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          First name *
                        </label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                            errors.firstName ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                          }`}
                          placeholder="Enter first name"
                        />
                        <AnimatePresence>
                          {errors.firstName && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="text-red-400 text-xs mt-1 flex items-center gap-1"
                            >
                              <AlertCircle className="w-3 h-3" />
                              {errors.firstName}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          Last name *
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                            errors.lastName ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                          }`}
                          placeholder="Enter last name"
                        />
                        <AnimatePresence>
                          {errors.lastName && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="text-red-400 text-xs mt-1 flex items-center gap-1"
                            >
                              <AlertCircle className="w-3 h-3" />
                              {errors.lastName}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Work Email */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Work Email *
                      </label>
                      <input
                        type="email"
                        value={formData.workEmail}
                        onChange={(e) => handleInputChange('workEmail', e.target.value)}
                        className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                          errors.workEmail ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                        }`}
                        placeholder="your.email@company.com"
                      />
                      <AnimatePresence>
                        {errors.workEmail && (
                          <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-400 text-xs mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {errors.workEmail}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Company or institution *
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                          errors.company ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                        }`}
                        placeholder="Your company or institution"
                      />
                      <AnimatePresence>
                        {errors.company && (
                          <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-400 text-xs mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {errors.company}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Phone and Country */}
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          Phone number
                        </label>
                        <input
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-black/50 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 hover:border-emerald-400/50"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">
                          Country *
                        </label>
                        <select
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                            errors.country ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                          }`}
                        >
                          {countries.map(country => (
                            <option key={country} value={country} className="bg-black text-white">
                              {country}
                            </option>
                          ))}
                        </select>
                        <AnimatePresence>
                          {errors.country && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="text-red-400 text-xs mt-1 flex items-center gap-1"
                            >
                              <AlertCircle className="w-3 h-3" />
                              {errors.country}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Reason for contacting */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Reason for contacting us *
                      </label>
                      <select
                        value={formData.reason}
                        onChange={(e) => handleInputChange('reason', e.target.value)}
                        className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 ${
                          errors.reason ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                        }`}
                      >
                        {reasonOptions.map(reason => (
                          <option key={reason} value={reason} className="bg-black text-white">
                            {reason}
                          </option>
                        ))}
                      </select>
                      <AnimatePresence>
                        {errors.reason && (
                          <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-400 text-xs mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {errors.reason}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Message * ({formData.message.length}/5000)
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        rows={3}
                        maxLength={5000}
                        className={`w-full px-3 py-2 text-sm bg-black/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 resize-vertical ${
                          errors.message ? 'border-red-500' : 'border-white/20 hover:border-emerald-400/50'
                        }`}
                        placeholder="Please describe your inquiry or requirements..."
                      />
                      <AnimatePresence>
                        {errors.message && (
                          <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-400 text-xs mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {errors.message}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* How did you hear */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        How did you hear about Godquantum Systems?
                      </label>
                      <select
                        value={formData.howDidYouHear}
                        onChange={(e) => handleInputChange('howDidYouHear', e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-black/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 hover:border-emerald-400/50"
                      >
                        {howDidYouHearOptions.map(option => (
                          <option key={option} value={option} className="bg-black text-white">
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Privacy Consent */}
                    <div>
                      <label className="flex items-start gap-2 cursor-pointer text-xs">
                        <input
                          type="checkbox"
                          checked={formData.privacyConsent}
                          onChange={(e) => handleInputChange('privacyConsent', e.target.checked)}
                          className="mt-1 h-4 w-4 text-emerald-400 focus:ring-emerald-400 border-white/20 rounded bg-black/50"
                        />
                        <span className="text-gray-300">
                          By submitting the form I understand and consent to the{' '}
                          <a href="/privacy-policy" className="text-emerald-400 hover:text-emerald-300 underline">
                            privacy policy
                          </a>. *
                        </span>
                      </label>
                      <AnimatePresence>
                        {errors.privacyConsent && (
                          <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-400 text-xs mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {errors.privacyConsent}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-3 text-sm font-semibold relative overflow-hidden mt-8 mb-4"
                      whileHover={{ 
                        scale: 1.02,
                        boxShadow: "0 0 30px rgba(0, 255, 209, 0.4)"
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <AnimatePresence mode="wait">
                        {isSubmitting ? (
                          <motion.div
                            key="submitting"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center justify-center"
                          >
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            >
                              <Loader2 className="w-4 h-4 mr-2" />
                            </motion.div>
                            Submitting...
                          </motion.div>
                        ) : (
                          <motion.div
                            key="submit"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center justify-center"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Submit
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>

                    <AnimatePresence>
                      {submitStatus === 'error' && (
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="text-center py-2"
                        >
                          <p className="text-red-400 flex items-center justify-center gap-2 text-xs">
                            <AlertCircle className="w-4 h-4" />
                            There was an error submitting your form. Please try again.
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.form>
                )}
              </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactModal;